import React from 'react'
import { connect } from 'react-redux'
import { GetProjects_projects } from '@chegg-wt/core/queries/GetProjects'
import { AppState } from '@chegg-wt/core/state'
import { SearchProjectsByName_projects } from '@chegg-wt/core/queries/SearchProjectsByName'
import SearchResultsEmpty from '@chegg-wt/core/components/SearchResults/SearchResultsEmpty'
import { useRoutesContext } from '@chegg-wt/core/components/RoutesContext'
import { EMPTY_RESULTS_MESSAGE } from '@chegg-wt/core/components/BaseProjectList/constants'
import { addRedirectQueryToRoute } from '@chegg-wt/core/utils/routes'
import { EmptyCitationsImage } from '@chegg-wt/core/components/SearchResults/styled'

import { CitationsExperience } from '@chegg-wt/core/types/citation-flow'
import { origins } from '@chegg-wt/core/state/instrumentation/rio/clickstreamInteractions'
import { Dispatch } from 'redux'
import { LoadingDots } from '@chegg-wt/core/components/LoadingDots'
import { PREMIUM_CITATIONS } from '@chegg-wt/core/constants/citationFlow'
import { userClick } from '@chegg-wt/core/state/instrumentation/actions'
import { isGuestSelector } from '@chegg-wt/core/state/user/selectors'
import { authModalExpFlagSelector } from '@chegg-wt/core/state/createCitation/selectors'
import { startAuthFlow } from '@chegg-wt/core/state/user/actions'
import AppLink from '../AppLink'
import { AuthModalOrigin } from '../Modals/AuthModal/types'
import { CTA_SIGN_IN, CTA_SIGN_UP, EMPTY_PROJECT_LIST, PROJECT_LIST } from './testIds'
import * as Styled from './styled'
import BaseProjectListItem from './BaseProjectListItem'

export type Project = GetProjects_projects | SearchProjectsByName_projects
export interface BaseProjectListProps {
  onCitationListClick?: () => void
  onCitationListDelete?: (project: Project) => void
  onCitationListEdit?: (project: Project) => void
  onUserClickCreateAccountCTA: (authModalExpFlag: boolean) => void
  onUserClickSignInCTA: (authModalExpFlag: boolean) => void
  citationsExperience: CitationsExperience
  limit?: number
  page?: number
  headerContent?: React.ReactNode
  footerContent?: React.ReactNode
  projects: Project[]
  isGuest: boolean
  hasSearchQuery?: boolean
  loading?: boolean
  error?: boolean
  hideActionMenu?: boolean
  authModalExpFlag?: boolean
}

const BaseProjectList: React.FC<BaseProjectListProps> = ({
  onCitationListClick,
  onCitationListDelete,
  onCitationListEdit,
  onUserClickCreateAccountCTA,
  onUserClickSignInCTA,
  citationsExperience,
  hideActionMenu = false,
  headerContent,
  footerContent,
  projects,
  hasSearchQuery,
  isGuest,
  loading,
  error,
  authModalExpFlag,
}) => {
  const { toSignup, toLogin, toCitationList, toBibliography } = useRoutesContext()
  const currentLocation = window.location.href
  const upgradeSignUpRoute = addRedirectQueryToRoute(toSignup(), currentLocation)
  const upgradeSignInRoute = addRedirectQueryToRoute(toLogin(), currentLocation)

  if (loading) {
    return (
      <Styled.ListContainer>
        {headerContent}
        <LoadingDots message="Loading your projects" />
      </Styled.ListContainer>
    )
  }
  if ((!projects?.length && hasSearchQuery) || error) {
    return (
      <Styled.ListContainer>
        {headerContent}
        <SearchResultsEmpty
          heading={EMPTY_RESULTS_MESSAGE}
          image={<EmptyCitationsImage />}
        />
      </Styled.ListContainer>
    )
  }

  if (!projects?.length) {
    return (
      <Styled.ListContainer>
        {headerContent}
        <Styled.TextContainer>
          {isGuest ? (
            <>
              {authModalExpFlag ? (
                <Styled.CtaAnchor
                  onClick={() => onUserClickCreateAccountCTA(authModalExpFlag)}
                  data-test={CTA_SIGN_UP}
                >
                  Create account
                </Styled.CtaAnchor>
              ) : (
                <AppLink route={upgradeSignUpRoute}>
                  <Styled.CtaLink
                    onClick={() => onUserClickCreateAccountCTA(authModalExpFlag)}
                    data-test={CTA_SIGN_UP}
                  >
                    Create account
                  </Styled.CtaLink>
                </AppLink>
              )}
              &nbsp;or&nbsp;
              {authModalExpFlag ? (
                <Styled.CtaAnchor
                  onClick={() => onUserClickSignInCTA(authModalExpFlag)}
                  data-test={CTA_SIGN_IN}
                  aria-label="Sign in to create citations"
                >
                  sign in
                </Styled.CtaAnchor>
              ) : (
                <AppLink route={upgradeSignInRoute}>
                  <Styled.CtaLink
                    onClick={() => onUserClickSignInCTA(authModalExpFlag)}
                    data-test={CTA_SIGN_IN}
                    aria-label="Sign in to create citations"
                  >
                    sign in
                  </Styled.CtaLink>
                </AppLink>
              )}
              &nbsp;to create unlimited citations
            </>
          ) : (
            <Styled.EmptyCitationList data-test={EMPTY_PROJECT_LIST}>
              You don&apos;t have any citation lists yet
            </Styled.EmptyCitationList>
          )}
        </Styled.TextContainer>
      </Styled.ListContainer>
    )
  }

  const projectItems = projects.map((project, index) => (
    <li key={project.id}>
      <BaseProjectListItem
        onClick={onCitationListClick}
        onDelete={() => onCitationListDelete(project)}
        onEdit={() => onCitationListEdit(project)}
        hideActionMenu={hideActionMenu}
        key={project.id}
        name={project.name}
        date={project.updatedAt}
        index={index}
        route={
          citationsExperience === PREMIUM_CITATIONS
            ? toCitationList({ projectId: project.id })
            : toBibliography({ projectId: project.id, style: project.defaultStyle })
        }
      />
    </li>
  ))

  return (
    <Styled.ListContainer data-test={PROJECT_LIST}>
      {headerContent}
      <Styled.List>{projectItems}</Styled.List>
      {footerContent}
    </Styled.ListContainer>
  )
}

const mapStateToProps = (state: AppState) => ({
  isGuest: isGuestSelector(state),
  authModalExpFlag: authModalExpFlagSelector(state),
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onUserClickCreateAccountCTA: (authModalExpFlag: boolean) => {
    dispatch(userClick({ origin: origins.citationsDashCreateAccountCitationListCTA }))
    if (authModalExpFlag) {
      dispatch(
        startAuthFlow({
          defaultFlow: 'signup',
          origin: AuthModalOrigin.DASHBOARD_PAGE_WIDGET,
        })
      )
    }
  },
  onUserClickSignInCTA: (authModalExpFlag: boolean) => {
    dispatch(userClick({ origin: origins.citationsDashSignInCitationListCTA }))
    if (authModalExpFlag) {
      dispatch(
        startAuthFlow({
          defaultFlow: 'signin',
          origin: AuthModalOrigin.DASHBOARD_PAGE_WIDGET,
        })
      )
    }
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(BaseProjectList)
